/**
 * HoneypotField — the canonical bot-detection decoy (SEC-HONEYPOT-001).
 *
 * A honeypot is a field a human never sees and never fills, but an automated
 * form-filler populates. A filled honeypot is therefore strong evidence of a bot.
 *
 * ## Why the details below are the way they are
 *
 * Each one was a deliberate choice in the implementation this was promoted from,
 * and each is load-bearing:
 *
 *   - **`<textarea>`, not `<input>`.** Password managers auto-fill inputs and
 *     universally skip textareas. An `<input>` honeypot therefore fires on real
 *     users with a password manager — false positives that look exactly like bot
 *     traffic and are almost impossible to diagnose from the server side.
 *   - **`autoComplete="nope"`, not `"off"`.** Browsers ignore `off` for fields
 *     with common names (`website`, `email`, …) and helpfully fill them anyway.
 *     A nonsense token is honoured where `off` is not.
 *   - **`tabIndex={-1}` + `aria-hidden`.** A keyboard user must never land on it
 *     and a screen reader must never announce it. Hiding it from sighted users
 *     alone would make this an accessibility trap rather than a bot trap.
 *   - **Visually hidden, not `display:none`.** Many bots skip fields that are
 *     `hidden` or `display:none`; an off-screen field stays in the layout tree
 *     and gets filled.
 *
 * ## `name` is REQUIRED, deliberately
 *
 * This component was promoted from six divergent copies whose defaults had split
 * two ways (`pf_hp_field` in four apps, `website` in two) — and 52 of the 56 call
 * sites omitted the prop, so every one of them silently inherited whichever
 * default its app happened to carry. The server-side check reads a field by NAME
 * (`createSubmissionHandler`'s `honeypotField`, default `'website'`), so a
 * mismatch between the two is a honeypot that quietly never fires.
 *
 * Making the prop required puts that contract at the call site, where it can be
 * read against the server it posts to, instead of in a default nobody looks at.
 *
 * ## Two detection modes, and this supports both
 *
 *   1. CLIENT-side — hold a ref and call `isFilled()` before submitting.
 *      Name-agnostic.
 *   2. SERVER-side — submit the field with the form and let the handler check
 *      `req.body[name]`. Name-SENSITIVE; `name` must match the server's
 *      `honeypotField`.
 *
 * ## Its home is form-security (DTOWNRETAIL-SITE-001, the push-14 certification)
 *
 * Promoted here from page-shell's `components/security/` byte for byte: the decoy is a form-security
 * primitive (`<SecureForm>` composes it and exports `HONEYPOT_FIELD_NAME`), and page-shell's barrel
 * drags `react-router-dom` in, which a React Native consumer of this package (palm-native's login
 * throttle, through `./core`) can never declare. page-shell re-exports it, identity-pinned, so every
 * existing `import { HoneypotField } from '@puffin-core/page-shell'` holds.
 *
 * @module @puffin-core/form-security/HoneypotField
 */

import { useState, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * The decoy's props.
 *
 * @typedef {object} HoneypotFieldProps
 * @property {string} name - REQUIRED. Must match the server's `honeypotField`
 *   when server-side detection is used. See the module docblock.
 * @property {string} [className] - Extra classes for the wrapper.
 * @property {(detection: {fieldName: string, value: string}) => void} [onBotDetected] - Called the
 *   moment the decoy is filled.
 */

/**
 * What the decoy's ref exposes: the textarea itself, augmented with `isFilled()`.
 *
 * @typedef {HTMLTextAreaElement & {isFilled: () => boolean}} HoneypotHandle
 */

/**
 * A hidden decoy field for bot detection.
 *
 * The JSDoc sits on the function `forwardRef` receives: on the `const` it typed nothing, so every prop read as
 * missing from `{}`.
 */
const HoneypotField = forwardRef(
  /**
   * @param {HoneypotFieldProps} props
   * @param {React.Ref<HoneypotHandle>} ref - Exposes the element, augmented with `isFilled()`.
   * @returns {React.ReactElement}
   */
  function HoneypotField({ name, className = '', onBotDetected }, ref) {
    const [value, setValue] = useState('');
    const inputRef = useRef(null);

    const handleChange = useCallback(
      (event) => {
        const nextValue = event.target.value;
        setValue(nextValue);
        if (nextValue && onBotDetected) {
          onBotDetected({ fieldName: name, value: nextValue });
        }
      },
      [name, onBotDetected],
    );

    /**
     * Read from React state rather than the DOM node: a controlled value is what
     * the component actually rendered, so it cannot disagree with what a
     * subsequent submit would send.
     *
     * @returns {boolean}
     */
    const isFilled = useCallback(() => value.length > 0, [value]);

    // The callback runs during commit, after React has attached the ref, so
    // `inputRef.current` is always the element here — no null guard is reachable.
    useImperativeHandle(ref, () => {
      const element = inputRef.current;
      element.isFilled = isFilled;
      return element;
    }, [isFilled]);

    const wrapperClassName = ['sr-only', className].filter(Boolean).join(' ').trim();

    return (
      <div className={wrapperClassName} data-testid="honeypot-wrapper">
        <textarea
          ref={inputRef}
          name={name}
          value={value}
          onChange={handleChange}
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="nope"
          rows={1}
          data-testid="honeypot-field"
          data-honeypot="true"
        />
      </div>
    );
  },
);

HoneypotField.propTypes = {
  /** REQUIRED — must match the server's `honeypotField` for server-side checks. */
  name: PropTypes.string.isRequired,
  /** Extra classes for the visually-hidden wrapper. */
  className: PropTypes.string,
  /** Called with `{fieldName, value}` when the decoy is filled. */
  onBotDetected: PropTypes.func,
};

export { HoneypotField };
export default HoneypotField;
